import {useState, useMemo} from 'react'
import {useI18n} from '@/i18n'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Checkbox} from '@/components/ui/checkbox'
import {Button} from '@/components/ui/button'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Tooltip, TooltipContent, TooltipTrigger} from '@/components/ui/tooltip'
import {cn} from '@/lib/utils'
import {AppLocalDate} from '@/lib/date-utils'
import type {DesignerAction, LayoutField, LayoutSchema, AttributeLayoutField, Palette} from '@/types/form-layout'

// ============================================================================
// Template expression preview
// ============================================================================

const MOCK_CONTEXT: Record<string, string> = {
    department: 'Engineering',
    costCenter: 'CC-100',
    today: AppLocalDate.today(),
}

function resolveExpression(expr: string): {resolved: string; hasUnknown: boolean; unknownToken: string | null} {
    const tokenRegex = /\$\{([^}]+)\}/g
    let hasUnknown = false
    let unknownToken: string | null = null

    const resolved = expr.replace(tokenRegex, (match, token) => {
        if (MOCK_CONTEXT[token] !== undefined) {
            return MOCK_CONTEXT[token]
        }
        hasUnknown = true
        unknownToken = match
        return ''
    })

    return {resolved, hasUnknown, unknownToken}
}

// ============================================================================
// ColSpan preset buttons
// ============================================================================

const COL_SPAN_PRESETS = [
    {labelKey: 'colSpanFull' as const, value: 12},
    {labelKey: 'colSpanHalf' as const, value: 6},
    {labelKey: 'colSpanThird' as const, value: 4},
    {labelKey: 'colSpanQuarter' as const, value: 3},
] as const

// Required override options — technical identifiers, not displayed as-is
const REQUIRED_OVERRIDE_OPTIONS = ['inherit', 'required', 'optional'] as const
type RequiredOverrideOption = (typeof REQUIRED_OVERRIDE_OPTIONS)[number]

// ============================================================================
// FieldProperties
// ============================================================================

interface FieldPropertiesProps {
    schema: LayoutSchema
    sectionKey: string
    fieldIndex: number
    dispatch: React.Dispatch<DesignerAction>
    palette: Palette | null
}

export function FieldProperties({schema, sectionKey, fieldIndex, dispatch, palette}: FieldPropertiesProps) {
    const i18n = useI18n()
    const [colSpanCustom, setColSpanCustom] = useState(false)

    const section = schema.sections.find((s) => s.key === sectionKey)
    const field = section?.fields[fieldIndex]

    // useMemo must be called before any conditional return (hooks rules)
    const templateExpr = field?.templateExpression ?? ''
    const templatePreview = useMemo(
        () => (templateExpr ? resolveExpression(templateExpr) : {resolved: '', hasUnknown: false, unknownToken: null}),
        [templateExpr]
    )

    if (!field) return null

    const patch = (p: Partial<LayoutField>) =>
        dispatch({type: 'UPDATE_FIELD', sectionKey, fieldIndex, patch: p})

    // Check if underlying definition has required: true
    let definitionRequired = false
    if (field.source === 'attribute' && palette) {
        const attrField = field as AttributeLayoutField
        const def =
            palette.attributeDefinitions.find((d) => d.id === attrField.definitionId) ??
            palette.readOnlyFields.find((d) => d.id === attrField.definitionId)
        definitionRequired = def?.required ?? false
    }

    const {resolved, hasUnknown, unknownToken} = templatePreview

    // Entity fields for visibility condition
    const entityFields = palette?.entityFields ?? []

    const visibilityCondition = field.visibilityCondition ?? null

    const currentColSpan = field.colSpan ?? 6
    const isPreset = COL_SPAN_PRESETS.some((p) => p.value === currentColSpan)

    const requiredOverrideLabelMap: Record<RequiredOverrideOption, () => string> = {
        inherit: () => i18n.formLayouts.requiredInherit,
        required: () => i18n.formLayouts.requiredRequired,
        optional: () => i18n.formLayouts.requiredOptional,
    }
    const requiredOverrideLabel = (opt: RequiredOverrideOption) => requiredOverrideLabelMap[opt]()

    return (
        <div className="space-y-4">
            {/* Label override */}
            <div className="space-y-1.5">
                <Label htmlFor="field-label">{i18n.formLayouts.fieldLabel}</Label>
                <Input
                    data-testid="field-label-input"
                    id="field-label"
                    value={field.label ?? ''}
                    onChange={(e) => patch({label: e.target.value || undefined})}
                    placeholder={i18n.formLayouts.fieldLabelPlaceholder}
                />
            </div>

            {/* Placeholder */}
            <div className="space-y-1.5">
                <Label htmlFor="field-placeholder">{i18n.formLayouts.fieldPlaceholder}</Label>
                <Input
                    data-testid="field-placeholder-input"
                    id="field-placeholder"
                    value={field.placeholder ?? ''}
                    onChange={(e) => patch({placeholder: e.target.value || undefined})}
                />
            </div>

            {/* Default value */}
            <div className="space-y-1.5">
                <Label htmlFor="field-default">{i18n.formLayouts.fieldDefaultValue}</Label>
                <Input
                    data-testid="field-default-input"
                    id="field-default"
                    value={field.defaultValue ?? ''}
                    onChange={(e) => patch({defaultValue: e.target.value || undefined})}
                />
            </div>

            {/* Template expression */}
            <div className="space-y-1.5">
                <Label htmlFor="field-template">{i18n.formLayouts.fieldTemplateExpression}</Label>
                <Input
                    data-testid="field-template-input"
                    id="field-template"
                    value={field.templateExpression ?? ''}
                    onChange={(e) => patch({templateExpression: e.target.value || undefined})}
                    /* eslint-disable-next-line i18next/no-literal-string */
                    placeholder="${template.department}"
                />
                {templateExpr && (
                    <p className={cn('text-xs', hasUnknown ? 'text-amber-600' : 'text-muted-foreground')}>
                        {hasUnknown
                            ? i18n.formLayouts.templateUnknownToken({token: unknownToken ?? ''})
                            : i18n.formLayouts.templatePreview({value: resolved})}
                    </p>
                )}
            </div>

            {/* ColSpan presets */}
            <div className="space-y-1.5">
                <Label>{i18n.formLayouts.fieldColSpan}</Label>
                <div className="flex gap-1 flex-wrap">
                    {COL_SPAN_PRESETS.map((preset) => (
                        <Button
                            key={preset.value}
                            data-testid={`col-span-preset-${preset.value}`}
                            variant={currentColSpan === preset.value && !colSpanCustom ? 'default' : 'outline'}
                            size="sm"
                            className="text-xs"
                            onClick={() => {
                                patch({colSpan: preset.value})
                                setColSpanCustom(false)
                            }}
                        >
                            {i18n.formLayouts[preset.labelKey]}
                        </Button>
                    ))}
                    <Button
                        data-testid="col-span-custom-btn"
                        variant={colSpanCustom || (!isPreset && currentColSpan > 0) ? 'default' : 'outline'}
                        size="sm"
                        className="text-xs"
                        onClick={() => setColSpanCustom(true)}
                    >
                        {i18n.formLayouts.colSpanCustom}
                    </Button>
                </div>
                {(colSpanCustom || (!isPreset && currentColSpan > 0)) && (
                    <Input
                        data-testid="col-span-custom-input"
                        type="number"
                        min={1}
                        max={12}
                        value={currentColSpan}
                        onChange={(e) => {
                            const v = Math.min(12, Math.max(1, Number(e.target.value)))
                            patch({colSpan: v})
                        }}
                        className="w-24 mt-1"
                    />
                )}
            </div>

            {/* Required override (attribute fields only) */}
            {field.source === 'attribute' && (
                <div className="space-y-1.5">
                    <Label>{i18n.formLayouts.fieldRequiredOverride}</Label>
                    <div className="flex gap-1">
                        {REQUIRED_OVERRIDE_OPTIONS.map((opt) => {
                            const isDisabled = opt === 'optional' && definitionRequired
                            const attrField = field as AttributeLayoutField
                            return (
                                <Tooltip key={opt}>
                                    <TooltipTrigger asChild>
                                        <Button
                                            data-testid={`required-override-${opt}`}
                                            variant={attrField.requiredOverride === opt ? 'default' : 'outline'}
                                            size="sm"
                                            className="text-xs capitalize"
                                            disabled={isDisabled}
                                            onClick={() => !isDisabled && patch({requiredOverride: opt})}
                                        >
                                            {requiredOverrideLabel(opt)}
                                        </Button>
                                    </TooltipTrigger>
                                    {isDisabled && (
                                        <TooltipContent>{i18n.formLayouts.requiredOptionalDisabledTooltip}</TooltipContent>
                                    )}
                                </Tooltip>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Conditional visibility */}
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="visibility-enabled"
                        checked={visibilityCondition !== null && visibilityCondition !== undefined}
                        onCheckedChange={(checked) => {
                            if (checked) {
                                patch({visibilityCondition: {fieldKey: '', equalTo: ''}})
                            } else {
                                patch({visibilityCondition: undefined})
                            }
                        }}
                    />
                    <Label htmlFor="visibility-enabled" className="cursor-pointer text-sm">
                        {i18n.formLayouts.visibilityShowWhen}
                    </Label>
                </div>

                {visibilityCondition && (
                    <>
                        <Select
                            value={visibilityCondition.fieldKey}
                            onValueChange={(val) =>
                                patch({visibilityCondition: {...visibilityCondition, fieldKey: val}})
                            }
                        >
                            <SelectTrigger data-testid="visibility-field-key-select" className="text-sm">
                                <SelectValue placeholder={i18n.formLayouts.visibilityFieldKeyPlaceholder} />
                            </SelectTrigger>
                            <SelectContent>
                                {entityFields.map((ef) => (
                                    <SelectItem key={ef.fieldKey} value={ef.fieldKey}>
                                        {ef.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Input
                            value={visibilityCondition.equalTo}
                            onChange={(e) =>
                                patch({visibilityCondition: {...visibilityCondition, equalTo: e.target.value}})
                            }
                            placeholder={i18n.formLayouts.visibilityValuePlaceholder}
                            className="text-sm"
                        />

                        <p className="text-xs text-muted-foreground">{i18n.formLayouts.visibilityHint}</p>
                    </>
                )}
            </div>

            {/* Disabled */}
            <div className="flex items-center gap-2">
                <Checkbox
                    id="field-disabled"
                    checked={field.disabled ?? false}
                    onCheckedChange={(checked) => patch({disabled: !!checked || undefined})}
                />
                <Label htmlFor="field-disabled" className="cursor-pointer text-sm">
                    {i18n.formLayouts.fieldDisabled}
                </Label>
            </div>

            {/* Hidden */}
            <div className="flex items-center gap-2">
                <Checkbox
                    id="field-hidden"
                    checked={field.hidden ?? false}
                    onCheckedChange={(checked) => patch({hidden: !!checked || undefined})}
                />
                <Label htmlFor="field-hidden" className="cursor-pointer text-sm">
                    {i18n.formLayouts.fieldHidden}
                </Label>
            </div>
        </div>
    )
}
